import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs/operators';

export const PAGE_TITLE = '@NOMIDIO_ANGULAR:PAGE_TITLE';

/**
 * A page title formatter function.
 *
 * @example
 * (base, page) => `${page} - ${base}` // Log in - My App
 *
 * @param base The title for the application
 * @param page The title for the specific page being visited.
 * @returns The formatted title for this page.
 */
export type PageTitleFormatFn = (base: string, page: string) => string;

/**
 * This is the Page Title Service. It will manage your page titles for you, meaning the window/tab title in the browser.
 *
 * To use it, include `NomidioPageTitleModule` in your application module imports and switch this service on in the AppComponent.
 * Then, in individual routes, specify a page title in that route's data object.
 *
 * The Page Title Service will use the narrowest page title it can find in the routing tree. This means it will check the exact route
 * the user is at first, then the parent of that route, then the parent of *that* route, and so on up to the application routing root.
 *
 * You can set a custom root title with {@link PageTitleService.setBaseTitle}, but by default it will pick up the application title
 * that was defined at the time the service was constructed—meaning in your `src/index.html` file.
 *
 * @example // Switching the service on —
 * export class AppComponent {
 *   constructor(
 *     private readonly pageTitle: PageTitleService,
 *   ) {
 *     this.pageTitle.on();
 *   }
 * }
 *
 * @example // Route definition —
 * import { PAGE_TITLE } from '../../../shared/page-title';
 * {
 *   path: 'login',
 *   // loadChildren or component something
 *   data: {
 *     [PAGE_TITLE]: 'Log in',
 *   }
 * }
 */
@Injectable()
export class PageTitleService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly titleService = inject(Title);

  /**
   * The default page title format: `${base} · ${page}`.
   *
   * @example // How this formatter looks:
   * pageTitle.setBaseTitle('My App');
   * pageTitle.setTitle('Login');
   * // My app · Login
   *
   * @example // Reverting to the default formatter
   * pageTitle.pageTitleFormatter = pageTitle.DEFAULT_TITLE_FORMAT_FN;
   */
  public readonly DEFAULT_TITLE_FORMAT_FN: PageTitleFormatFn = (base, page) => `${base} · ${page}`;

  /**
   * This function determines the format for page titles. Define this function to use a non-default title format.
   *
   * If no specific page title is available, only the base title is used and the formatter is ignored completely.
   *
   * @example // Setting the page title format to something else
   * pageTitle.pageTitleFormatter = (base, page) => `${page} | ${base}`;
   *
   * @see DEFAULT_TITLE_FORMAT_FN The default formatter
   */
  public pageTitleFormatter: PageTitleFormatFn = this.DEFAULT_TITLE_FORMAT_FN;
  private baseTitle: string;
  private active = false;

  constructor() {
    this.baseTitle = this.titleService.getTitle().trim();
    this.router.events.pipe(
      filter(() => this.active),
      filter(event => event instanceof NavigationEnd),
      map(() => this.findDeepestTitle(this.activatedRoute)),
      map(pageTitle => this.formatPageTitle(pageTitle))
    ).subscribe((pageTitle: string) => {
      this.titleService.setTitle(pageTitle);
    });
  }

  /**
   * Set the base title, which will be used throughout the app as the 'root' title.
   *
   * @example
   * pageTitle.setBaseTitle('My App');
   * // then load a page with a page title of 'First Page'
   * // page title: 'My App · First Page'
   *
   *
   * @param baseTitle The base title to use
   */
  public setBaseTitle(baseTitle: string): void {
    this.baseTitle = baseTitle;
  }

  /**
   * Set the title for this page manually. Use this when it can't be set at the routing module.
   *
   * @example
   * pageTitle.setBaseTitle('My App');
   * pageTitle.setTitle('First Page');
   * // page title: 'My App · First Page'
   *
   * @see `PageTitleService.on()` for automatic title tracking
   *
   * @param title The title to use for this page.
   */
  public setTitle(pageTitle: string): void {
    const formatted = this.formatPageTitle(pageTitle);
    this.titleService.setTitle(formatted);
  }

  /**
   * Set the page title, overriding the base/page format.
   *
   * This is equivalent to Angular's `Title.setTitle()`, but is provided so you don't have to import both services.
   *
   * @example
   * pageTitle.setBaseTitle('My App');
   * pageTitle.setFullTitle('First Page');
   * // page title: 'First Page', without a base title
   *
   * @see `PageTitleService.setTitle()` for preserving the base/page format.
   *
   * @param title The full page title to use
   */
  public setFullTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  /**
   * Switch the service's page title management feature on. It's off by default.
   */
  public on(): void {
    this.active = true;
  }

  /**
   * Switch the service's page title management feature off.
   * It will stop managing page titles and will restore the page title to the current known base title.
   */
  public off(): void {
    this.active = false;
    this.titleService.setTitle(this.baseTitle);
  }

  /**
   * Format the page title according to the format specified for this service.
   *
   * @param pageTitle The page title to insert into the page title format
   * @returns The formatted page title
   */
  private formatPageTitle(pageTitle: string | null): string {
    if (pageTitle) {
      return this.pageTitleFormatter(this.baseTitle, pageTitle);
    } else {
      return this.baseTitle;
    }
  }

  private findDeepestTitle(route: ActivatedRoute): string | null {
    const pageTitle = route.snapshot.data?.[PAGE_TITLE] ?? null;

    if (route.firstChild) {
      const deeperTitle = this.findDeepestTitle(route.firstChild);
      return deeperTitle ?? pageTitle;
    } else {
      return pageTitle;
    }
  }
}
